import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';

@Component({
    selector: 'modal',
    template: `
        <div bsModal #bsModalRef="bs-modal" #modalRoot class="modal" [ngClass]="modalClass" [class.fade]="animation" tabindex="-1" role="dialog"
             (onHide)="_onHide()" (onHidden)="_onHidden()">
            <div class="modal-dialog" [ngClass]="{ 'modal-sm': isSmall(), 'modal-lg': isLarge() }">
                <div class="modal-content">
                    <ng-content></ng-content>
                </div>
            </div>
        </div>
    `,
    standalone: false
})
export class ModalComponent implements OnDestroy {

    // Backed by ngx-bootstrap's ModalDirective (BS5 dropped the BS4 jQuery
    // `.modal` plugin). Public API (open/close/dismiss/hide/onClose) is unchanged
    // so the ~12 consuming components need no edits.
    @ViewChild('bsModalRef') bsModalRef: ModalDirective;
    @ViewChild('modalRoot') modalRoot: ElementRef<HTMLElement>;
    id: string = uniqueId('modal_');
    result: ModalResult = ModalResult.None;
    hiding = false;
    overrideSize: string = null;
    @Input() animation = true;
    @Input() size: string;
    // Optional class(es) placed on the `.modal` element itself. Because the modal
    // is relocated to <body> on open (see below), it leaves the component's
    // encapsulation subtree, so `:host`-anchored styles no longer reach it. Pass
    // a class here and target it globally (e.g. `::ng-deep .my-modal .modal-dialog`)
    // to style the relocated dialog.
    @Input() modalClass: string;
    @Output() onClose: EventEmitter<any> = new EventEmitter();

    // Where the `.modal` element normally lives in the component view, so we can
    // put it back when the modal closes.
    private modalHomeParent: HTMLElement = null;

    constructor(private renderer: Renderer2) {}

    // ngx-bootstrap's ModalDirective renders the modal element IN PLACE. When an
    // ancestor establishes a stacking context (e.g. layout `.content-wrap` is
    // position:relative; z-index:0), the in-place `.modal` — even at z-index 1055
    // — is painted inside that context, so the body-level `.modal-backdrop`
    // (z-index 1050) covers it and swallows all clicks (ESC still works). Move the
    // modal to <body> while it is open so it shares the backdrop's stacking
    // context, then restore it to its home when it closes.
    private detachToBody(): void {
        const el = this.modalRoot && this.modalRoot.nativeElement;
        if (el && el.parentElement && el.parentElement !== document.body) {
            this.modalHomeParent = el.parentElement;
            this.renderer.appendChild(document.body, el);
        }
    }

    private restoreHome(): void {
        const el = this.modalRoot && this.modalRoot.nativeElement;
        if (el && this.modalHomeParent && el.parentElement === document.body) {
            this.renderer.appendChild(this.modalHomeParent, el);
        }
        this.modalHomeParent = null;
    }

    ngOnDestroy(): void {
        // If torn down while open (e.g. route change), drop the relocated node so
        // it doesn't leak on <body>.
        const el = this.modalRoot && this.modalRoot.nativeElement;
        if (el && el.parentElement === document.body) {
            this.renderer.removeChild(document.body, el);
        }
    }

    // ngx-bootstrap fires (onHide) as the modal begins hiding (backdrop click,
    // ESC, or programmatic hide) and (onHidden) once it is gone.
    _onHide(): void {
        this.hiding = true;
        if (this.result === ModalResult.None) { this.dismiss(); }
        this.result = ModalResult.None;
    }

    _onHidden(): void {
        this.hiding = false;
        this.overrideSize = null;
        this.restoreHome();
    }

    open(size?: string): void {
        if (ModalSize.validSize(size)) { this.overrideSize = size; }
        this.detachToBody();
        this.bsModalRef.show();
    }

    close(): void {
        this.result = ModalResult.Close;
        this.onClose.next(this.result);
        this.hide();
    }

    dismiss(): void {
        this.result = ModalResult.Dismiss;
        this.onClose.next(this.result);
        this.hide();
    }

    public hide(): void {
        if (!this.hiding) { this.bsModalRef.hide(); }
    }

    public isSmall(): boolean {
        return this.overrideSize !== ModalSize.Large && this.size === ModalSize.Small || this.overrideSize === ModalSize.Small;
    }

    public isLarge(): boolean {
        return this.overrideSize !== ModalSize.Small && this.size === ModalSize.Large || this.overrideSize === ModalSize.Large;
    }
}

export class ModalSize {
    static Small = 'sm';
    static Large = 'lg';

    static validSize(size: string): boolean {
        return size && (size === ModalSize.Small || size === ModalSize.Large);
    }
}

export enum ModalResult {
    None,
    Close,
    Dismiss
}

let id = 0;
export function uniqueId(prefix: string): string {
    return prefix + ++id;
}
